import { ChevronDown, ChevronUp, GripVertical, Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface PickerItem {
	id: number;
	label: string;
	sub?: string;
}

interface Props {
	title: string;
	hint: string;
	items: PickerItem[];
	onChange: (items: PickerItem[]) => void;
	search: (q: string) => Promise<PickerItem[]>;
	ordered?: boolean;
}

const move = <T,>(list: T[], from: number, to: number): T[] => {
	if (to < 0 || to >= list.length || from === to) return list;
	const next = [...list];
	const [item] = next.splice(from, 1);
	next.splice(to, 0, item);
	return next;
};

export const OrderedPicker = ({
	title,
	hint,
	items,
	onChange,
	search,
	ordered = true,
}: Props) => {
	const [term, setTerm] = useState("");
	const [results, setResults] = useState<PickerItem[]>([]);
	const [searching, setSearching] = useState(false);
	const dragIndex = useRef<number | null>(null);

	useEffect(() => {
		const q = term.trim();
		if (q.length < 2) {
			setResults([]);
			return;
		}
		let cancelled = false;
		setSearching(true);
		const timer = setTimeout(() => {
			search(q)
				.then((found) => {
					if (!cancelled) setResults(found);
				})
				.catch(() => {
					if (!cancelled) setResults([]);
				})
				.finally(() => {
					if (!cancelled) setSearching(false);
				});
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [term, search]);

	const add = useCallback(
		(item: PickerItem) => {
			if (items.some((i) => i.id === item.id)) return;
			onChange([...items, item]);
			setTerm("");
			setResults([]);
		},
		[items, onChange],
	);

	const remove = useCallback(
		(id: number) => onChange(items.filter((i) => i.id !== id)),
		[items, onChange],
	);

	const reorder = useCallback(
		(from: number, to: number) => onChange(move(items, from, to)),
		[items, onChange],
	);

	return (
		<div className="space-y-2">
			<div className="flex items-baseline justify-between">
				<h4 className="text-sm font-semibold text-slate-700">{title}</h4>
				<span className="text-xs text-slate-400">{items.length} selected</span>
			</div>

			<div className="relative">
				<input
					value={term}
					onChange={(e) => setTerm(e.target.value)}
					placeholder={hint}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-primary"
				/>
				{term.trim().length >= 2 && (
					<div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
						{searching && (
							<p className="px-3 py-2 text-[13px] text-slate-400">Searching…</p>
						)}
						{!searching && results.length === 0 && (
							<p className="px-3 py-2 text-[13px] text-slate-400">No matches</p>
						)}
						{results.map((r) => (
							<button
								key={r.id}
								type="button"
								onClick={() => add(r)}
								disabled={items.some((i) => i.id === r.id)}
								className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40"
							>
								<Plus className="h-3.5 w-3.5 shrink-0 text-slate-400" />
								<span className="truncate">{r.label}</span>
								{r.sub && (
									<span className="ml-auto shrink-0 text-xs text-slate-400">
										{r.sub}
									</span>
								)}
							</button>
						))}
					</div>
				)}
			</div>

			{items.length === 0 ? (
				<p className="text-[13px] text-slate-400">Nothing selected yet.</p>
			) : (
				<ol className="space-y-1">
					{items.map((item, index) => (
						<li
							key={item.id}
							draggable={ordered}
							onDragStart={() => {
								dragIndex.current = index;
							}}
							onDragOver={(e) => e.preventDefault()}
							onDrop={(e) => {
								e.preventDefault();
								if (dragIndex.current !== null)
									reorder(dragIndex.current, index);
								dragIndex.current = null;
							}}
							className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[13px]"
						>
							{ordered && (
								<>
									<GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-300" />
									<span className="w-5 shrink-0 text-right text-xs font-medium text-slate-400">
										{index + 1}
									</span>
								</>
							)}
							<span className="truncate text-slate-700">{item.label}</span>
							{item.sub && (
								<span className="ml-auto shrink-0 text-xs text-slate-400">
									{item.sub}
								</span>
							)}
							<div
								className={`flex shrink-0 items-center gap-0.5 ${item.sub ? "" : "ml-auto"}`}
							>
								{ordered && (
									<>
										<button
											type="button"
											aria-label="Move up"
											disabled={index === 0}
											onClick={() => reorder(index, index - 1)}
											className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
										>
											<ChevronUp className="h-3.5 w-3.5" />
										</button>
										<button
											type="button"
											aria-label="Move down"
											disabled={index === items.length - 1}
											onClick={() => reorder(index, index + 1)}
											className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
										>
											<ChevronDown className="h-3.5 w-3.5" />
										</button>
									</>
								)}
								<button
									type="button"
									aria-label="Remove"
									onClick={() => remove(item.id)}
									className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500"
								>
									<X className="h-3.5 w-3.5" />
								</button>
							</div>
						</li>
					))}
				</ol>
			)}
		</div>
	);
};
